/**
 * Fictional cast for the Google OAuth verification recording.
 * Every email uses the reserved .example domain so nothing here can be a real inbox.
 */

export const VERIFICATION_CAST = {
  user: {
    fullName: 'Sam Carter',
    username: 'sam.carter',
    email: 'sam.carter@waymark.example',
    company: 'Waymark Studio',
    location: 'Port Nelson',
    website: 'https://waymark.example',
    bio: 'Studio lead at Waymark. This profile is sample data for a product recording.',
  },
  organization: {
    name: 'Waymark Studio',
  },
  project: {
    name: 'Riverbend Launch',
  },
  sampleEvent: {
    title: 'Coffee with Alex Rivera',
    location: 'Port Nelson Roastery',
    description:
      'Sample event created by FlowState Automate to demonstrate Google Calendar access. Everyone named here is fictional.',
  },
  sampleTask: {
    title: 'Send the Riverbend proposal',
    notes:
      'Sample task created by FlowState Automate to demonstrate Google Tasks access. Fictional checklist item for Waymark Studio.',
  },
  sampleDoc: {
    title: 'Waymark meeting notes',
    body: 'Attendees: Sam Carter, Alex Rivera, and Jordan Kim. Sample document created by FlowState Automate to demonstrate Google Docs access. All names are fictional.',
  },
} as const;

export interface VerificationClientSeed {
  id: string;
  name: string;
  email: string;
  company: string;
  industry: string;
  lifecycle_stage: 'customer' | 'prospect' | 'lead' | 'subscriber' | 'inactive';
  priority: 'high' | 'medium' | 'low' | 'critical';
  tier?: 'gold' | 'silver' | 'bronze';
  estimated_value: number;
  phone?: string;
}

/** Same ids as the existing demo scenarios so pipeline rows keep their links. */
export const VERIFICATION_CLIENTS: VerificationClientSeed[] = [
  {
    id: 'demo-client-techcorp',
    name: 'Alex Rivera',
    email: 'alex.rivera@techcorp.example',
    company: 'TechCorp Solutions',
    industry: 'Technology',
    lifecycle_stage: 'customer',
    priority: 'high',
    tier: 'gold',
    estimated_value: 85000,
    phone: '+1 555-0101',
  },
  {
    id: 'demo-client-greenleaf',
    name: 'Jordan Kim',
    email: 'jordan.kim@greenleaf.example',
    company: 'GreenLeaf Marketing',
    industry: 'Marketing',
    lifecycle_stage: 'prospect',
    priority: 'medium',
    tier: 'silver',
    estimated_value: 42000,
  },
  {
    id: 'demo-client-northstar',
    name: 'Priya Patel',
    email: 'priya.patel@northstar.example',
    company: 'Northstar Logistics',
    industry: 'Logistics',
    lifecycle_stage: 'customer',
    priority: 'high',
    estimated_value: 128000,
    tier: 'gold',
  },
  {
    id: 'demo-client-aurora',
    name: 'Casey Morgan',
    email: 'casey.morgan@aurora.example',
    company: 'Aurora Health Group',
    industry: 'Healthcare',
    lifecycle_stage: 'prospect',
    priority: 'critical',
    estimated_value: 96000,
    tier: 'gold',
  },
  {
    id: 'demo-client-summit',
    name: 'Riley Chen',
    email: 'riley.chen@summit.example',
    company: 'Summit Financial',
    industry: 'Finance',
    lifecycle_stage: 'customer',
    priority: 'high',
    estimated_value: 156000,
    tier: 'gold',
  },
  {
    id: 'demo-client-brightpath',
    name: 'Morgan Lee',
    email: 'morgan.lee@brightpath.example',
    company: 'BrightPath Education',
    industry: 'Education',
    lifecycle_stage: 'lead',
    priority: 'low',
    estimated_value: 22000,
  },
  {
    id: 'demo-client-coastal',
    name: 'Taylor Brooks',
    email: 'taylor.brooks@coastal.example',
    company: 'Coastal Builders',
    industry: 'Construction',
    lifecycle_stage: 'lead',
    priority: 'medium',
    estimated_value: 67000,
    tier: 'bronze',
  },
  {
    id: 'demo-client-vertex',
    name: 'Jamie Ortiz',
    email: 'jamie.ortiz@vertex.example',
    company: 'Vertex Analytics',
    industry: 'SaaS',
    lifecycle_stage: 'subscriber',
    priority: 'low',
    estimated_value: 12000,
  },
  {
    id: 'demo-client-harbor',
    name: 'Quinn Walsh',
    email: 'quinn.walsh@harbor.example',
    company: 'Harbor Retail Co',
    industry: 'Retail',
    lifecycle_stage: 'inactive',
    priority: 'medium',
    estimated_value: 34000,
  },
  {
    id: 'demo-client-pioneer',
    name: 'Drew Simmons',
    email: 'drew.simmons@pioneer.example',
    company: 'Pioneer Legal Partners',
    industry: 'Legal',
    lifecycle_stage: 'prospect',
    priority: 'high',
    estimated_value: 54000,
    tier: 'silver',
  },
];

export const VERIFICATION_TASKS = [
  {
    id: 'studio-task-1',
    title: 'Send the Riverbend proposal',
    status: 'In progress',
    owner: 'Sam Carter',
  },
  {
    id: 'studio-task-2',
    title: 'Book the coffee with Alex Rivera',
    status: 'To do',
    owner: 'Sam Carter',
  },
  {
    id: 'studio-task-3',
    title: 'Review the Harbor Retail contract',
    status: 'Done',
    owner: 'Jordan Kim',
  },
] as const;

export const VERIFICATION_NOTIFICATIONS = [
  {
    id: 'studio-note-1',
    title: 'Coffee tomorrow',
    message: 'Coffee with Alex Rivera is on the Waymark calendar.',
  },
  {
    id: 'studio-note-2',
    title: 'Task due',
    message: 'Jordan Kim asked you to send the Riverbend proposal.',
  },
] as const;
